<template>
	<div>
		<!-- add invoice category dialog: -->
		<v-dialog
			v-model="dialog"
			persistent
			max-width="500"
			content-class="rounded-medium"
		>
			<v-form ref="form" :disabled="submitLoading" @submit.prevent="submit">
				<v-card>
					<v-card-title>
						{{!invoiceCategoryId ? $t('add') : $t('edit')}} {{$t('invoiceCategories.invoice-category')}}
					</v-card-title>
					<v-card-text class="pb-2">
						<v-row dense>
							<!-- name -->
							<v-col cols="12" md="6">
								<v-text-field
									v-model="submitData.categoryName"
									:label="$t('inputs.invoice-category-name')"
									outlined
									dense
									hide-details
									:rules="[rules.required]"
								></v-text-field>
							</v-col>

							<!-- customer account -->
							<v-col cols="12" md="6">
								<v-autocomplete
									v-model="submitData.account"
									:label="$t('inputs.account')"
									:items="configs.accounts.filter(c =>
										c.type === type.sub &&
										!defaultFinancialFileAccountsIds.includes(c.id)
									)"
									item-value="id"
									item-text="name"
									outlined
									dense
									hide-details
								></v-autocomplete>
							</v-col>

							<!-- invoice type -->
							<v-col cols="12">
								<v-autocomplete
									v-model="submitData.useCategoryFor"
									:label="$t('invoiceCategories.use-category-for')"
									:items="invoicesTypes"
									item-text="name"
									item-value="id"
									multiple
									hide-details
									outlined
									dense
									:rules="[val => (val || []).length > 0]"
									@change="submitData.setDefault = []"
								></v-autocomplete>
							</v-col>

							<!-- sales_account -->
							<v-col v-if="submitData.useCategoryFor.includes(invoiceType.sale)" cols="12" md="6">
								<v-autocomplete
									v-model="submitData.salesAccount"
									:label="$t('inputs.sales-account')"
									:items="tradeAndBudgetAccounts"
									item-text="name"
									item-value="id"
									hide-details
									outlined
									dense
								></v-autocomplete>
							</v-col>

							<!-- purchases_account -->
							<v-col v-if="submitData.useCategoryFor.includes(invoiceType.buy)" cols="12" md="6">
								<v-autocomplete
									v-model="submitData.purchasesAccount"
									:label="$t('inputs.purchasing-account')"
									:items="tradeAndBudgetAccounts"
									item-text="name"
									item-value="id"
									hide-details
									outlined
									dense
								></v-autocomplete>
							</v-col>

							<!-- sales_refund_account -->
							<v-col v-if="submitData.useCategoryFor.includes(invoiceType.saleRefund)" cols="12" md="6">
								<v-autocomplete
									v-model="submitData.salesRefundAccount"
									:items="tradeAndBudgetAccounts"
									:label="$t('inputs.sales-refund-account')"
									item-text="name"
									item-value="id"
									hide-details
									outlined
									dense
								></v-autocomplete>
							</v-col>

							<!-- purchases_refund_account -->
							<v-col v-if="submitData.useCategoryFor.includes(invoiceType.buyRefund)" cols="12" md="6">
								<v-autocomplete
									v-model="submitData.purchasesRefundAccount"
									:items="tradeAndBudgetAccounts"
									:label="$t('inputs.purchasing-refund-account')"
									item-text="name"
									item-value="id"
									hide-details
									outlined
									dense
								></v-autocomplete>
							</v-col>

							<!-- set default for -->
							<v-col v-if="submitData.useCategoryFor.length" cols="12">
								<v-autocomplete
									v-model="submitData.setDefaultFor"
									:label="$t('invoiceCategories.set-a-default-for')"
									:items="invoicesTypes.filter(c => submitData.useCategoryFor.includes(c.id))"
									item-text="name"
									item-value="id"
									hide-details
									outlined
									dense
									multiple
								></v-autocomplete>
							</v-col>

							<!-- notes -->
							<v-col cols="12">
								<v-textarea
									v-model="submitData.notes"
									:label="$t('inputs.notes')"
									outlined
									rows="2"
									auto-grow
									dense
									hide-details
								></v-textarea>
							</v-col>
							<v-col v-if="invoiceCategoryMaterialsOnly" cols="auto" class="pb-0 px-2">
								<v-checkbox
									v-model="submitData.isMaterialOnly"
									label="مواد فقط "
									hide-details
									dense
								>

								</v-checkbox>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<!-- create -->
						<v-btn text color="success" type="submit" :loading="submitLoading">
							{{$t('save')}}
						</v-btn>

						<!-- cancel -->
						<v-btn text color="danger-color" :disabled="submitLoading" @click="dialog = false">
							{{$t('cancel')}}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- delete dialog -->
		<v-dialog 
			v-model="deleteDialog"
			persistent
			max-width="350"
			content-class="rounded-medium"
		>
			<v-card>
				<v-card-title>
					<!-- {{ $t('invoiceCategories.delete-invoice-category') }} -->
				</v-card-title>
				<v-card-text class="pb-1">
					<!-- {{ $t('invoiceCategories.delete-warn') }} -->
				</v-card-text>
				<v-card-actions>
					<v-btn
						text
						color="success"
						@click="deleteInvoiceCategory(categoryId)"
					>
						{{$t('delete')}}
					</v-btn>
					<v-btn
						text
						color="danger-color"
						@click="deleteDialog = false"
					>
						{{$t('cancel')}}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-container fluid>
			<v-row>
				<v-col xl="10" md="9" cols="12">
					<!-- The header of the page -->
					<div class="d-flex align-center mb-2">
						<slot name="breadcrumbs"></slot>
						<v-spacer></v-spacer>
						<v-btn color="success" @click="dialog = true">
							<v-icon class="me-1">mdi-plus-circle-outline</v-icon>
							{{$t('invoiceCategories.add-invoice-category')}}
						</v-btn>
					</div>

					<!-- table -->
					<bee-handy-table
						:headers="headers"
						:items="loading ? [] : invoiceCategories"
						:items-per-page="20"
						:height="$vuetify.breakpoint.height - 160"
						fixed-header
						zebra
						dense
						pagination-on-scroll
						:loading="loading"
						:data-table-props="{ disableSort: true }"
						hide-shown-rows-text="auto"
					>
						<!-- trade accounts -->
						<template v-slot:item.tradeAccounts="{ item }">
							<!-- sales-account -->
							<div
								v-if="item.sales_account_id"
								class="d-flex flex-wrap text-caption mb-1"
							>
								<span class="grey--text text--darken-1 font-weight-medium me-1">
									{{$t('invoice-type.sales')}}:
								</span>
								<span>{{getAccountById(item.sales_account_id)?.name}}</span>
							</div>
							
							<!-- purchasing-account -->
							<div
								v-if="item.purchases_account_id"
								class="d-flex flex-wrap text-caption mb-1"
							>
								<span class="grey--text text--darken-1 font-weight-medium me-1">
									{{$t('invoice-type.purchases')}}:
								</span>
								<span>{{getAccountById(item.purchases_account_id)?.name}}</span>
							</div>
							
							<!-- sales-refund-account -->
							<div
								v-if="item.sales_refund_account_id"
								class="d-flex flex-wrap text-caption mb-1"
							>
								<span class="grey--text text--darken-1 font-weight-medium me-1">
									{{$t('invoice-type.sales-refund')}}:
								</span>
								<span>{{getAccountById(item.sales_refund_account_id)?.name}}</span>
							</div>
							
							<!-- purchasing-refund-account -->
							<div
								v-if="item.purchases_refund_account_id"
								class="d-flex flex-wrap text-caption mb-1"
							>
								<span class="grey--text text--darken-1 font-weight-medium me-1">
									{{$t('invoice-type.purchases-refund')}}:
								</span>
								<span>{{getAccountById(item.purchases_refund_account_id)?.name}}</span>
							</div>

							<!-- empty state -->
							<div
								v-if="
									!item.sales_account_id &&
									!item.purchases_account_id &&
									!item.sales_refund_account_id &&
									!item.purchases_refund_account_id
								"
							>-</div>
						</template>
						
						<!-- use category -->
						<template v-slot:item.useCategory="{ item }">
							<div
								class="text-pre-line text-body-2 font-weight-medium"
								style="line-height: 23px"
							>
								{{
									item.category_invoice_types.map(c =>
										invoicesTypes.find(e => e.id === c.invoice_type)?.name
									).filter(Boolean).join('\n') || '-'
								}}
							</div>
						</template>
						
						<template v-slot:item.actions="{ item }">
							<div class="d-flex">
								<!-- edit -->
								<tooltip top :text="$t('tooltip.edit')">
									<v-btn
										height="34"
										width="38"
										min-width="38"
										color="edit-color white--text"
										class="me-1"
										@click="invoiceCategoryId = item.id; dialog = true;"
									>
										<v-icon>mdi-pencil</v-icon>
									</v-btn>
								</tooltip>

								<!-- delete -->
								<tooltip v-if="false" top :text="$t('tooltip.delete')"> 
									<v-btn
										height="34"
										width="38"
										min-width="38"
										color="danger-dark-color white--text"
										class="me-1"
										@click="invoiceCategoryId = item.id; deleteDialog = true;"
									>
										<v-icon>mdi-delete</v-icon>
									</v-btn>
								</tooltip>
							</div>
						</template>
					</bee-handy-table>
				</v-col>

				<v-divider vertical inset/>

				<v-col xl="2" md="3" cols="12">
					<v-card class="px-4 py-2 rounded-medium">
						<div class="text-body-1 font-weight-bold mb-1">
							{{$t('invoiceCategories.default-invoices-categories')}}
						</div>
						<v-row class="my-0">
							<!-- default-sales-type -->
							<v-col md="12" sm="6" cols="12" class="pa-2">
								<v-autocomplete
									v-model="defaultCategories.sales"
									:label="$t('invoiceCategories.default-sales-category')"
									:items="invoiceCategories.filter(c =>
										c.category_invoice_types
											.map(e => e.invoice_type)
											.includes(invoiceType.sale)
									)"
									item-text="name"
									item-value="id"
									dense
									outlined
									hide-details
									@change="updateDefaultsInvoiceCategories"
								></v-autocomplete>
							</v-col>

							<!-- default-purchase-type -->
							<v-col md="12" sm="6" cols="12" class="pa-2">
								<v-autocomplete
									v-model="defaultCategories.purchases"
									:label="$t('invoiceCategories.default-purchases-category')"
									:items="invoiceCategories.filter(c =>
										c.category_invoice_types
											.map(e => e.invoice_type)
											.includes(invoiceType.buy)
									)"
									item-text="name"
									item-value="id"
									dense
									outlined
									hide-details
									@change="updateDefaultsInvoiceCategories"
								></v-autocomplete>
							</v-col>

							<!-- default-sales-refund-type -->
							<v-col md="12" sm="6" cols="12" class="pa-2">
								<v-autocomplete
									v-model="defaultCategories.salesRefund"
									:label="$t('invoiceCategories.default-sales-refund-category')"
									:items="invoiceCategories.filter(c =>
										c.category_invoice_types
											.map(e => e.invoice_type)
											.includes(invoiceType.saleRefund)
									)"
									item-text="name"
									item-value="id"
									dense
									outlined
									hide-details
									@change="updateDefaultsInvoiceCategories"
								></v-autocomplete>
							</v-col>

							<!-- default-purchases-refund-type -->
							<v-col md="12" sm="6" cols="12" class="pa-2">
								<v-autocomplete
									v-model="defaultCategories.purchasesRefund"
									:label="$t('invoiceCategories.default-purchases-refund-category')"
									:items="invoiceCategories.filter(c =>
										c.category_invoice_types
											.map(e => e.invoice_type)
											.includes(invoiceType.buyRefund)
									)"
									item-text="name"
									item-value="id"
									dense
									outlined
									hide-details
									@change="updateDefaultsInvoiceCategories"
								></v-autocomplete>
							</v-col>
						</v-row>
					</v-card>
				</v-col>
			</v-row>
		</v-container>
	</div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import { finalType, type, invoicesTypes, invoiceType } from '@/helpers/enums';
import { invoiceCategoryMaterialsOnly } from '@/configs/global'
import rules from '@/validation';
import eventBus from '@/event-bus';

export default {
	data() {
		return {
			// loadings:
			loading: false,
			submitLoading: false,

			// dialogs
			dialog: false,
			deleteDialog: false,

			invoiceCategoryId: null,

			// rules
			rules: rules.rules,
			type,
			invoiceType,
			invoicesTypes,
			invoiceCategoryMaterialsOnly,
			// data
			submitData: {
				categoryName: null,
				account: null,
				useCategoryFor: [],
				salesAccount: null,
				purchasesAccount: null,
				salesRefundAccount: null,
				purchasesRefundAccount: null,
				setDefaultFor: [],
				isMaterialOnly: false,
				notes: null,
			},

			defaultCategories: {
				sales: null,
				purchases: null,
				salesRefund: null,
				purchasesRefund: null,
			}
		}
	},

	watch: {
		dialog(val) {
			if (val) {
				if (this.invoiceCategoryId) {
					const invoiceCategory = this.getInvoiceCategoryById(this.invoiceCategoryId);
					
					this.submitData.categoryName = invoiceCategory.name;
					this.submitData.account = invoiceCategory.customer_account_id;
					this.submitData.useCategoryFor =
						invoiceCategory.category_invoice_types.map(c => c.invoice_type);
					this.submitData.salesAccount = invoiceCategory.sales_account_id;
					this.submitData.purchasesAccount = invoiceCategory.purchases_account_id;
					this.submitData.salesRefundAccount = invoiceCategory.sales_refund_account_id;
					this.submitData.purchasesRefundAccount = invoiceCategory.purchases_refund_account_id;
					this.submitData.setDefaultFor =
						invoiceCategory.category_invoice_types
							.filter(c => c.is_default_category)
							.map(c => c.invoice_type);
					this.submitData.notes = invoiceCategory.notes;
					this.submitData.isMaterialOnly = invoiceCategory.is_materials_only
				}
			} else {
				this.invoiceCategoryId = null;
				this.$refs.form.reset();
			}
		}
	},

	computed: {
		...mapState({
			configs: state => state.configs.configs,
			invoiceCategories: state => state.invoiceCategories.invoiceCategories,
			defaultInvoiceCategories: state => state.invoiceCategories.defaultInvoiceCategories,
			defaultFinancialFileAccounts: state => state.configs.default_financial_file_accounts,
		}),
		...mapGetters({
			getAccountById: 'configs/getAccountById',
			getInvoiceCategoryById: 'invoiceCategories/getInvoiceCategoryById',
			getSettingByKey: 'settings/getSettingByKey'
		}),

		headers() {
			const headers = [
				{
					text: this.$t('headers.invoice-category-name'),
					value: 'name',
					name: 'name',
				},
				{
					text: this.$t('headers.account'),
					value: 'customer_account_id',
					name: 'customerAccount',
					setValueExpr: value => this.getAccountById(value)?.name
				},
				{
					text: this.$t('headers.trade-accounts'),
					value: 'tradeAccounts',
					name: 'tradeAccounts',
					width: '25%'
				},
				{
					text: this.$t('invoiceCategories.use-category-for'),
					value: 'useCategory',
					name: 'useCategory',
					width: '15%'
				},
				{
					text: this.$t('inputs.notes'),
					value: 'notes',
					name: 'notes',
					cellClass: 'text-caption font-weight-bold',
				},
				{
					text: '',
					value: 'actions',
					name: 'actions',
					width: 40
				}
			];
			for (let i = 0; i < headers.length; i++) {
				headers[i].columnClass = headers[i].columnClass ? headers[i].columnClass + ' px-2' : 'px-2';
			}

			return headers;
		},

		defaultFinancialFileAccountsIds() {
			return Object.values(this.defaultFinancialFileAccounts).filter(Boolean);
		},

		tradeAndBudgetAccounts() {
			return this.configs.accounts.filter(c =>
				( this.accountFinalTypeRestrict ? c.final_type === finalType.trade: true) &&
				c.type === type.sub &&
				// accounts without default accounts
				!this.defaultFinancialFileAccountsIds.includes(c.id)
			)
		},
		accountFinalTypeRestrict() {
			return this.getSettingByKey('ValidateAccountType')?.setting_value
		}
	},

	methods: {
		fetchInvoicesCategories(isFirstLoad = false) {
			this.loading = true;
			return this.$store.dispatch('invoiceCategories/fetchAll').finally(() => {
				if (!isFirstLoad) this.loading = false
			})
		},

		fetchDefaultInvoicesCategories() {
			return this.$store.dispatch('invoiceCategories/fetchDefaultCategories').finally(() => {
				console.log(this.defaultInvoiceCategories);
				console.log(this.defaultInvoiceCategories
						.find(c => c.invoice_type === invoiceType.sale)?.category_id);
				console.log(this.defaultInvoiceCategories
						.find(c => c.invoice_type === invoiceType.buy)?.category_id);
				console.log(this.defaultInvoiceCategories
						.find(c => c.invoice_type === invoiceType.saleRefund)?.category_id);
				console.log(this.defaultInvoiceCategories
						.find(c => c.invoice_type === invoiceType.buyRefund)?.category_id);
				
				this.defaultCategories.sales =
					this.defaultInvoiceCategories
						.find(c => c.invoice_type === invoiceType.sale)?.category_id;
				this.defaultCategories.purchases =
					this.defaultInvoiceCategories
						.find(c => c.invoice_type === invoiceType.buy)?.category_id;
				this.defaultCategories.salesRefund =
					this.defaultInvoiceCategories
						.find(c => c.invoice_type === invoiceType.saleRefund)?.category_id;
				this.defaultCategories.purchasesRefund =
					this.defaultInvoiceCategories
						.find(c => c.invoice_type === invoiceType.buyRefund)?.category_id;
			})
		},

		submit() {
			if (this.$refs.form.validate()) {
				this.submitLoading = true;
				const actionName = !this.invoiceCategoryId ? 'create' : 'update';
				this.$store.dispatch(`invoiceCategories/${actionName}`, {
					id: this.invoiceCategoryId,
					name: this.submitData.categoryName,
					customerAccount: this.submitData.account,
					isMaterialOnly: this.submitData.isMaterialOnly,
					salesAccount: 
						this.submitData.useCategoryFor.includes(invoiceType.sale)
							? this.submitData.salesAccount
							: null,
					purchasesAccount: 
						this.submitData.useCategoryFor.includes(invoiceType.buy)
							? this.submitData.purchasesAccount
							: null,
					salesRefundAccount: 
						this.submitData.useCategoryFor.includes(invoiceType.saleRefund)
							? this.submitData.salesRefundAccount
							: null,
					purchasesRefundAccount: 
						this.submitData.useCategoryFor.includes(invoiceType.buyRefund)
							? this.submitData.purchasesRefundAccount
							: null,
					categoryInvoiceTypeRequests: this.submitData.useCategoryFor.map(invoiceType => ({
						invoice_type: invoiceType,
						is_default_category: this.submitData.setDefaultFor.includes(invoiceType)
					})),
					notes: this.submitData.notes,
				}).then(() => {
					this.fetchInvoicesCategories();
					this.fetchDefaultInvoicesCategories();
					eventBus.$emit(
                        'notification',
                        !this.invoiceCategoryId
                            ? this.$t('messages.adding-completed-successfully')
                            : this.$t('messages.editing-completed-successfully')
                    );
					this.dialog = false;
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		},

		updateDefaultsInvoiceCategories() {
			this.submitLoading = true;
			this.$store.dispatch('invoiceCategories/updateDefaultCategories', {
				defaultCategories: [
					{
						invoice_type: invoiceType.sale,
						category_id: this.defaultCategories.sales
					},
					{
						invoice_type: invoiceType.buy,
						category_id: this.defaultCategories.purchases
					},
					{
						invoice_type: invoiceType.saleRefund,
						category_id: this.defaultCategories.salesRefund
					},
					{
						invoice_type: invoiceType.buyRefund,
						category_id: this.defaultCategories.purchasesRefund
					},
				]
			}).then(() => {
				this.$store.dispatch('invoiceCategories/fetchDefaultCategories');
				eventBus.$emit('notification', this.$t('messages.editing-completed-successfully'));
			}).finally(() => {
				this.submitLoading = true;
			})
		},

		deleteInvoiceCategory(id) {
			this.loading = true
			this.$store.dispatch('invoiceCategories/delete', {
				categoryId: id
			}).then(() => {
				this.$store.dispatch('invoiceCategories/fetchAll')
				this.deleteDialog = false
			}).finally(() => {
				this.loading = false
			})
		}
	},
	created() {
		this.loading = true;
		Promise.all([
			!Object.keys(this.defaultFinancialFileAccounts).length
				? this.$store.dispatch('configs/fetchDefaultFinancialFileAccounts')
				: null,
			this.fetchInvoicesCategories(true),
			this.fetchDefaultInvoicesCategories()
		]).finally(() => {
			this.loading = false;
		});
	},
}
</script>